import { useRef, useState } from "react";
import { ImagePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";

export const ImageField = ({ label, value, onChange, testId = "image-field" }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div>
      {label && (
        <label className="mb-1.5 block font-heading text-xs font-semibold uppercase tracking-wide text-steel">
          {label}
        </label>
      )}
      <div className="flex items-center gap-3">
        <div className="grid h-20 w-28 shrink-0 place-items-center overflow-hidden rounded-lg border border-steel/25 bg-white">
          {value ? (
            <img src={value} alt="" className="h-full w-full object-contain" />
          ) : (
            <ImagePlus className="h-5 w-5 text-steel/50" />
          )}
        </div>
        <div className="min-w-0">
          <button
            type="button"
            data-testid={`${testId}-upload-btn`}
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-2 rounded-full border border-saffron/40 px-4 py-2 font-heading text-xs font-semibold text-saffron transition-colors hover:bg-saffron hover:text-white disabled:opacity-60"
          >
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}
            {uploading ? "Uploading…" : "Upload photo"}
          </button>
          {value && (
            <p className="mt-1 truncate font-body text-[11px] text-steel/70">{value}</p>
          )}
        </div>
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
      </div>
    </div>
  );
};

export const Field = ({ label, value, onChange, textarea = false, testId, type = "text" }) => (
  <div>
    <label className="mb-1.5 block font-heading text-xs font-semibold uppercase tracking-wide text-steel">
      {label}
    </label>
    {textarea ? (
      <textarea
        data-testid={testId}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        rows={4}
        className="w-full rounded-lg border border-steel/25 bg-white px-3 py-2 font-body text-sm text-ink outline-none transition-colors focus:border-saffron"
      />
    ) : (
      <input
        data-testid={testId}
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-steel/25 bg-white px-3 py-2 font-body text-sm text-ink outline-none transition-colors focus:border-saffron"
      />
    )}
  </div>
);
